import { Component, inject, input, OnDestroy, OnInit, output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DialogService, ShButtonComponent, ShInputTextComponent, TextValueAccessor } from '@gbr-monorepo/gbr-shui';
import { TranslateModule } from '@ngx-translate/core';
import { User } from '@gbr-monorepo/interfaces';
import { debounceTime, distinctUntilChanged, filter, Observable, Subscription, switchMap, tap } from 'rxjs';
import { TableButtonComponent } from './components/table-button/table-button.component';
import { TableComponent } from './components/table/table.component';
import { columns } from './config';
import { AddUserComponent } from './dialogs/add-user/add-user.component';
import { DeleteUserComponent, DeleteUserDialogData } from './dialogs/delete-user/delete-user.component';
import { EditUserComponent } from './dialogs/edit-user/edit-user.component';

type UserDialogResult = {
    confirmed: boolean;
    payload?: User;
};

@Component({
    selector: 'app-user-management',
    templateUrl: './user-management.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        TextValueAccessor,
        TableComponent,
        ShButtonComponent,
        ShInputTextComponent
    ]
})
export class UserManagementComponent implements OnInit, OnDestroy {
    private readonly dialog = inject(DialogService);
    private searchValueSubscription?: Subscription;

    protected readonly columns = columns.concat(
        {
            name: '',
            selector: 'edit',
            component: TableButtonComponent,
            componentInit: (row, instance) => {
                instance.text = 'user-management.buttons.edit';
                instance.translate = true;
                instance.type = 'primary';
                instance.buttonClick.pipe(switchMap(() => this.openEditUserDialog(row))).subscribe();
            },
            width: '2',
            unpadded: true
        },
        {
            name: '',
            selector: 'delete',
            component: TableButtonComponent,
            componentInit: (row, instance) => {
                instance.text = 'user-management.buttons.delete';
                instance.translate = true;
                instance.type = 'secondary';
                instance.buttonClick.pipe(switchMap(() => this.openDeleteUserDialog(row))).subscribe();
            },
            width: '2',
            unpadded: true
        }
    );

    protected readonly searchControl = new FormControl('', { nonNullable: true });

    public readonly users = input<User[]>([]);
    public readonly total = input<number>();
    public readonly isLoading = input<boolean>();
    public readonly delegatesEnabled = input(false);
    public readonly pageSizeOptions = input([10, 25, 50, 100]);
    public readonly page = input(1);
    public readonly searchChanged = output<string>();
    public readonly userAdded = output<User>();
    public readonly userEdited = output<User>();
    public readonly userDeleted = output<string>();
    public readonly pageChange = output<{
        page: number;
        pageSize: number;
    }>();

    public ngOnInit(): void {
        this.subscribeToSearchChanges();
    }

    private subscribeToSearchChanges(): void {
        this.searchValueSubscription = this.searchControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                tap(searchValue => this.searchChanged.emit(searchValue))
            )
            .subscribe();
    }

    private openEditUserDialog(user: User): Observable<unknown> {
        return this.dialog
            .open<UserDialogResult, { user: User; delegatesEnabled: boolean }>(EditUserComponent, {
                data: {
                    user,
                    delegatesEnabled: this.delegatesEnabled()
                },
                width: '500px'
            })
            .afterClosed.pipe(
                tap(result => {
                    const dialogResult = result as UserDialogResult | undefined;
                    if (dialogResult?.confirmed && dialogResult.payload) {
                        this.userEdited.emit(dialogResult.payload);
                    }
                })
            );
    }

    private openDeleteUserDialog({ gid, firstName, lastName }: User): Observable<unknown> {
        const data: DeleteUserDialogData = { gid, firstName, lastName };
        return this.dialog
            .open(DeleteUserComponent, {
                data,
                width: '500px'
            })
            .afterClosed.pipe(
                filter(result => result === 'delete'),
                tap(() => {
                    this.userDeleted.emit(gid);
                })
            );
    }

    public ngOnDestroy(): void {
        this.searchValueSubscription?.unsubscribe();
    }

    public goToCosmos(): void {
        window.open('https://cosmos.siemens-healthineers.com/', '_blank');
    }

    public onPageChange(event: any): void {
        const { page, size: pageSize } = event;
        this.pageChange.emit({ page, pageSize });
    }

    public openAddUserDialog(event: Event): void {
        event.stopPropagation();
        this.dialog
            .open<UserDialogResult>(AddUserComponent, {
                data: {
                    delegatesEnabled: this.delegatesEnabled()
                },
                width: '800px'
            })
            .afterClosed.pipe(
                tap(result => {
                    const dialogResult = result as UserDialogResult | undefined;
                    if (dialogResult?.confirmed && dialogResult.payload) {
                        this.userAdded.emit(dialogResult.payload);
                    }
                })
            )
            .subscribe();
    }
}
